'use client';

import {
  AnimatePresence,
  motion,
  MotionValue,
  useMotionTemplate,
  useTransform,
} from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { INSTAGRAM_URL, RESERVATION_URL, TIKTOK_URL } from "../lib/site";
import { NavigationItem } from "../types";
import { AnimatedButton } from "./AnimatedButton";

interface HeaderProps {
  items: NavigationItem[];
  scrollYProgress: MotionValue<number>;
  brandName?: string;
}

const clamp = (value: number, min: number, max: number) => {
  return Math.max(min, Math.min(value, max));
};

export const Header: React.FC<HeaderProps> = ({ items, scrollYProgress, brandName = "Barber" }) => {
  const [viewportWidth, setViewportWidth] = useState<number | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const updateViewportWidth = () => setViewportWidth(window.innerWidth);
    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth);
    return () => window.removeEventListener("resize", updateViewportWidth);
  }, []);

  const measuredViewportWidth = viewportWidth ?? 390;
  const isCompactNav = viewportWidth === null ? true : viewportWidth < 1280;

  const compactWidth = clamp(measuredViewportWidth - 20, 320, 980);
  const desktopStartWidth = clamp(measuredViewportWidth * 0.86, 820, 1320);
  const desktopEndWidth = clamp(desktopStartWidth - 52, 760, 1220);

  const startWidthPx = isCompactNav ? compactWidth : desktopStartWidth;
  const endWidthPx = isCompactNav ? compactWidth : desktopEndWidth;

  const startTopPx = isCompactNav ? 12 : 56;
  const endTopPx = isCompactNav ? 12 : 20;

  const startRadiusPx = isCompactNav ? 28 : 24;
  const endRadiusPx = isCompactNav ? 28 : 42;

  const width = useTransform(
    scrollYProgress,
    [0, 0.15],
    [startWidthPx, endWidthPx],
  );
  const top = useTransform(scrollYProgress, [0, 0.15], [startTopPx, endTopPx]);
  const borderRadius = useTransform(
    scrollYProgress,
    [0, 0.15],
    [startRadiusPx, endRadiusPx],
  );
  const backgroundColor = useTransform(
    scrollYProgress,
    [0, 0.15],
    isCompactNav
      ? ["rgba(24, 24, 24, 0.9)", "rgba(24, 24, 24, 0.9)"]
      : ["rgba(20, 20, 20, 0.72)", "rgba(20, 20, 20, 0.86)"],
  );

  const backdropFilterValue = isCompactNav ? "blur(16px)" : "blur(12px)";
  const borderAlpha = useTransform(
    scrollYProgress,
    [0, 0.15],
    isCompactNav ? [0.08, 0.08] : [0.15, 0.06],
  );
  const shadowAlpha = useTransform(
    scrollYProgress,
    [0, 0.15],
    isCompactNav ? [0.28, 0.28] : [0.12, 0.34],
  );
  const paddingX = useTransform(
    scrollYProgress,
    [0, 0.15],
    isCompactNav ? [16, 16] : [28, 28],
  );
  const paddingY = useTransform(
    scrollYProgress,
    [0, 0.15],
    isCompactNav ? [11, 11] : [15, 12],
  );

  const border = useMotionTemplate`1px solid rgba(255, 255, 255, ${borderAlpha})`;
  const boxShadow = useMotionTemplate`0 10px 30px rgba(0, 0, 0, ${shadowAlpha})`;

  useEffect(() => {
    if (!isCompactNav) {
      setMenuOpen(false);
    }
  }, [isCompactNav]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    }

    const onEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMenuOpen(false);
      }
    };

    window.addEventListener("keydown", onEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [menuOpen]);

  const mobileLinks = useMemo(() => {
    const defaults: NavigationItem[] = [
      { label: "Accueil", href: "#accueil" },
      ...items,
      { label: "Réservation", href: "#reservation" },
    ];

    const seen = new Set<string>();
    return defaults.filter((item) => {
      if (seen.has(item.href)) {
        return false;
      }
      seen.add(item.href);
      return true;
    });
  }, [items]);

  const handleMenuLinkClick = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none px-2 sm:px-4">
        <motion.header
          style={{
            width,
            top,
            borderRadius,
            backgroundColor,
            backdropFilter: backdropFilterValue,
            WebkitBackdropFilter: backdropFilterValue,
            border,
            color: "#ffffff",
            boxShadow,
            paddingLeft: paddingX,
            paddingRight: paddingX,
            paddingTop: paddingY,
            paddingBottom: paddingY,
          }}
          className="pointer-events-auto flex items-center justify-between transition-[background-color,border-color,box-shadow] duration-300 relative"
        >
          {isCompactNav ? (
            <>
              <a
                href="#accueil"
                className="z-20 whitespace-nowrap flex flex-col leading-none"
              >
                <span className="text-[8px] sm:text-[9px] uppercase tracking-[0.28em] text-white/80 mb-1">
                  {brandName}
                </span>
                <span className="text-[12px] sm:text-[14px] uppercase tracking-[0.24em] font-black">
                  Eleganze
                </span>
              </a>

              <div className="z-20 flex items-center gap-2 sm:gap-3">
                <AnimatedButton
                  href={RESERVATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  Réserver
                </AnimatedButton>

                <button
                  type="button"
                  onClick={() => setMenuOpen((prev) => !prev)}
                  aria-label={menuOpen ? "Fermer le menu" : "Ouvrir le menu"}
                  className="w-10 h-10 rounded-full border border-white/15 hover:border-white/35 transition-colors flex items-center justify-center"
                >
                  <div className="flex flex-col justify-center space-y-1.5 w-5">
                    <span className="block w-full h-0.5 bg-current" />
                    <span className="block w-full h-0.5 bg-current" />
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <a
                href="#accueil"
                className="flex items-center z-20 hover:opacity-80 transition-opacity"
              >
                <div className="flex flex-col leading-none">
                  <span className="text-[9px] uppercase tracking-[0.28em] text-white/75 mb-1">
                    {brandName}
                  </span>
                  <span className="text-base uppercase tracking-[0.22em] font-black whitespace-nowrap">
                    Eleganze
                  </span>
                </div>
              </a>

              <nav className="flex items-center gap-6 z-20">
                {items.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="text-sm font-semibold hover:opacity-70 transition-opacity drop-shadow-md whitespace-nowrap"
                  >
                    {item.label}
                  </a>
                ))}
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="inline-flex items-center justify-center text-white/90 hover:text-white transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm8.5 1.8h-8.5A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95Zm-4.25 2.95a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.8a3.45 3.45 0 1 0 0 6.9 3.45 3.45 0 0 0 0-6.9Zm5.55-2.11a1.21 1.21 0 1 1 0 2.42 1.21 1.21 0 0 1 0-2.42Z" />
                  </svg>
                </a>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  className="inline-flex items-center justify-center text-white/90 hover:text-white transition-colors"
                >
                  <svg
                    className="w-4 h-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                </a>
                <AnimatedButton
                  href={RESERVATION_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  Réserver
                </AnimatedButton>
              </nav>
            </>
          )}
        </motion.header>
      </div>

      <AnimatePresence>
        {isCompactNav && menuOpen && (
          <>
            <motion.button
              key="overlay"
              type="button"
              aria-label="Fermer le menu"
              className="fixed inset-0 bg-[#1d1d1d]/45 backdrop-blur-sm z-[60]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setMenuOpen(false)}
            />
            <motion.aside
              key="drawer"
              initial={{ opacity: 0, y: -18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -18, scale: 0.98 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="fixed top-24 left-3 right-3 z-[70] rounded-3xl border border-white/10 bg-[#1d1d1d]/90 text-white p-5 sm:p-6 shadow-2xl"
            >
              <div className="space-y-2">
                {mobileLinks.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    onClick={handleMenuLinkClick}
                    className="flex items-center justify-between rounded-2xl px-4 py-3 hover:bg-white/10 transition-colors text-sm sm:text-base font-semibold tracking-wide"
                  >
                    <span>{item.label}</span>
                    <svg
                      className="w-4 h-4 opacity-70"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M5 12h14M12 5l7 7-7 7" />
                    </svg>
                  </a>
                ))}
              </div>

              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-5 w-full rounded-2xl border border-white/15 px-4 py-3 inline-flex items-center justify-between hover:bg-white/10 transition-colors"
              >
                <span className="inline-flex items-center gap-3 text-sm sm:text-base font-semibold">
                  <svg
                    className="w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2Zm8.5 1.8h-8.5A3.95 3.95 0 0 0 3.8 7.75v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95Zm-4.25 2.95a5.25 5.25 0 1 1 0 10.5 5.25 5.25 0 0 1 0-10.5Zm0 1.8a3.45 3.45 0 1 0 0 6.9 3.45 3.45 0 0 0 0-6.9Zm5.55-2.11a1.21 1.21 0 1 1 0 2.42 1.21 1.21 0 0 1 0-2.42Z" />
                  </svg>
                  @barber_eleganze
                </span>
                <svg
                  className="w-4 h-4 opacity-70"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>

              <a
                href={TIKTOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 w-full rounded-2xl border border-white/15 px-4 py-3 inline-flex items-center justify-between hover:bg-white/10 transition-colors"
              >
                <span className="inline-flex items-center gap-3 text-sm sm:text-base font-semibold">
                  <svg
                    className="w-4 h-4 shrink-0"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
                  </svg>
                  @eleganze_barber
                </span>
                <svg
                  className="w-4 h-4 opacity-70"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
